<template>
  <table class="BookingRecapRoom BookingRecapRoom-grow">
    <ai-typo
      v-if="accommodation?.label"
      as="caption"
      variant="heading-04"
      class="BookingRecapRoom-title BookingRecapRoom-caption">
      {{ accommodation?.label }}
    </ai-typo>
    <thead class="BookingRecapRoom-caption">
      <tr>
        <th colspan="2">
          <ai-typo
            as="span"
            variant="paragraph-04-regular"
            class="BookingRecapRoom-title">
            <ai-typo as="span" variant="paragraph-04-bold">
              {{ $t('booking.recap.rooms.title', { index: index + 1 }) }}
            </ai-typo>
            <ai-typo as="span" variant="paragraph-04-regular">
              {{ guestsLabel }}
            </ai-typo>
          </ai-typo>
        </th>
      </tr>
    </thead>

    <tbody class="BookingRecapRoom-grow">
      <tr class="BookingRecapRoom-row BookingRecapRoom-totalCost">
        <ai-typo as="td" variant="paragraph-03-regular">
          {{ $t('booking.recap.rooms.totalCost') }}
        </ai-typo>

        <td nowrap="nowrap">
          <ai-amount
            :amount="convertedTotal.amount"
            :symbol="convertedTotal.currency?.code"
            decimal-typo="paragraph-04-bold"
            main-typo="paragraph-02-bold" />
        </td>
      </tr>
      <tr>
        <td colspan="2">
          <ai-expand
            v-model="breakdownExpanded"
            class="BookingRecapRoom-expand"
            :label-open="$t('booking.recap.rooms.showBreakdown')"
            :label-hide="$t('booking.recap.rooms.hideBreakdown')"
            :reverse="true">
            <div class="BookingRecapRoom-breakdown--reverse">
              <!-- Rate -->
              <table class="BookingRecapRoom-grow">
                <ai-typo
                  as="caption"
                  variant="paragraph-03-bold"
                  class="BookingRecapRoom-caption">
                  {{ $t('booking.recap.rooms.roomRate') }}
                </ai-typo>

                <tbody>
                  <tr
                    v-if="convertedOfferPrice.amount !== 0"
                    class="BookingRecapRoom-row">
                    <ai-typo as="td" variant="paragraph-04-regular">
                      {{ offer?.label }}
                    </ai-typo>

                    <td>
                      <ai-amount
                        :amount="convertedOfferPrice.amount"
                        :symbol="convertedOfferPrice.currency?.code"
                        decimal-typo="paragraph-04-bold"
                        main-typo="paragraph-02-bold" />
                    </td>
                  </tr>

                  <tr>
                    <td colspan="2">
                      <ai-button
                        ref="roomInformationButton"
                        variant="tertiary"
                        @click="openRateConditions">
                        <ai-typo
                          class="BookingRecapRoom-rateConditions"
                          as="span"
                          variant="button-bold-underline">
                          {{ $t('booking.recap.rooms.rateConditions') }}
                        </ai-typo>
                      </ai-button>
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- Enhancements -->
              <template v-if="options && options.length > 0">
                <ai-separator :margin="2" />

                <table class="BookingRecapRoom-grow">
                  <ai-typo
                    as="caption"
                    variant="paragraph-03-bold"
                    class="BookingRecapRoom-caption">
                    {{ $t('booking.recap.rooms.enhancements') }}
                  </ai-typo>
                  <tbody>
                    <tr
                      v-for="option in options"
                      :key="option.bookingOption.code as string"
                      class="BookingRecapRoom-row">
                      <ai-typo
                        v-if="getEnhancementPrice(option) !== 0"
                        as="td"
                        variant="paragraph-04-regular"
                        class="BookingRecapRoom-label">
                        {{ option.apiOption?.label }}
                      </ai-typo>

                      <td v-if="getEnhancementPrice(option) !== 0">
                        <ai-amount
                          :amount="
                            convertTo(
                              getEnhancementPrice(option) *
                                option.bookingOption.quantity,
                              currenciesMap[
                                option.apiOption?.pricing?.currency as string
                              ],
                              currenciesMap[bookingStore.currentCurrency],
                            )
                          "
                          :symbol="
                            currenciesMap[bookingStore.currentCurrency]?.code ??
                            option.apiOption?.pricing?.currency
                          "
                          decimal-typo="paragraph-04-bold"
                          main-typo="paragraph-02-bold" />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </template>

              <!-- Taxes -->
              <template v-if="feesTaxes || vatTaxes || otherTaxTaxes">
                <ai-separator :margin="2" />

                <table class="BookingRecapRoom-grow">
                  <ai-typo
                    as="caption"
                    variant="paragraph-03-bold"
                    class="BookingRecapRoom-caption">
                    {{ $t('booking.recap.rooms.taxesAndFees') }}
                  </ai-typo>
                  <tbody>
                    <!-- Fees -->
                    <tr v-if="feesTaxes && convertedFeesTaxes.amount !== 0">
                      <ai-typo as="td" variant="paragraph-04-regular">
                        {{ $t('booking.recap.rooms.fees') }}
                      </ai-typo>

                      <td class="BookingRecapRoom-alignRight">
                        <ai-amount
                          :amount="convertedFeesTaxes.amount"
                          :symbol="convertedFeesTaxes.currency?.code"
                          decimal-typo="paragraph-04-bold"
                          main-typo="paragraph-02-bold" />
                      </td>
                    </tr>

                    <!-- VAT -->
                    <tr v-if="vatTaxes && convertedVatTaxes.amount !== 0">
                      <ai-typo as="td" variant="paragraph-04-regular">
                        {{ $t('booking.recap.rooms.vat') }}
                      </ai-typo>

                      <td class="BookingRecapRoom-alignRight">
                        <ai-amount
                          :amount="convertedVatTaxes.amount"
                          :symbol="convertedVatTaxes.currency?.code"
                          decimal-typo="paragraph-04-bold"
                          main-typo="paragraph-02-bold" />
                      </td>
                    </tr>

                    <!-- Other taxes -->
                    <tr
                      v-if="otherTaxTaxes && convertedOtherTaxTaxes.amount !== 0">
                      <ai-typo as="td" variant="paragraph-04-regular">
                        {{ $t('booking.recap.rooms.otherTaxes') }}
                      </ai-typo>

                      <td class="BookingRecapRoom-alignRight">
                        <ai-amount
                          :amount="convertedOtherTaxTaxes.amount"
                          :symbol="convertedOtherTaxTaxes.currency?.code"
                          decimal-typo="paragraph-04-bold"
                          main-typo="paragraph-02-bold" />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </template>
            </div>
          </ai-expand>
        </td>
      </tr>
    </tbody>
  </table>

  <booking-rate-condition-drawer
    v-model="drawerOpen"
    :focus-on-close-element="roomInformationButton?.$el"
    :adults="adults"
    :children-ages="childrenAges"
    :date-in="bookingStore.dates.dateIn as string"
    :hotel-code="bookingStore.hotelId as string"
    :nights="bookingStore.dates.nights"
    :offer-code="currentOfferCode"
    :product-code="bookingRoom.productCode"
    :all-taxes-included="areAllTaxesIncluded"
    role="listitem" />
</template>

<script setup lang="ts">
import { computeAdultsAndChildren } from '../../helpers';
import type { BookingRoomOptionEnhancement } from '../../hooks';
import { useBookingRoomApiData } from '../../hooks';
import type { Room } from '../../interfaces';
import { useBookingStore } from '../../store';

import type { RoomOfferDetailFragment } from '~~/domains/graphql';
import { useRoomDetailsOffersLazyQuery } from '~~/domains/graphql';
import { useCurrenciesMap } from '~~/domains/referential';
import { useUserStore } from '~~/domains/user';
import { convertTo } from '~~/helpers';

type Props = {
  index: number;
  multiple?: boolean;
  bookingRoom: Room;
};

const props = defineProps<Props>();

const bookingStore = useBookingStore();
const userStore = useUserStore();
const { adults, childrenAges } = computeAdultsAndChildren(
  props.bookingRoom.adults,
  props.bookingRoom.childrenAges,
  bookingStore.maxChildrenAge,
);

const currenciesMap = useCurrenciesMap();

const areAllTaxesIncluded = useAreAllTaxesIncluded();

const { t } = useI18n();

const bookingRoom = computed(() => props.bookingRoom);
const wantSignup = computed(() => bookingStore.wantSignup);
const multipleRoom = computed(() => props.multiple);

const forceMemberOffer = computed(
  () => wantSignup.value && !multipleRoom.value,
);
const { accommodation, options, offer } = useBookingRoomApiData(
  bookingRoom,
  forceMemberOffer,
);

const { onResult, load, document } = useRoomDetailsOffersLazyQuery({
  adults,
  dateIn: bookingStore.dates.dateIn as string,
  hotelCode: bookingStore.hotelId as string,
  nights: bookingStore.dates.nights,
  offerCode:
    (userStore.isLogged || bookingStore.wantSignup) &&
    props.bookingRoom.memberOfferId &&
    !multipleRoom.value
      ? props.bookingRoom.memberOfferId
      : props.bookingRoom.publicOfferId,
  productCode: props.bookingRoom.productCode,
});

const breakdownExpanded = ref(false);
const drawerOpen = ref(false);
const rateProps = ref();

const roomInformationButton = ref<ComponentPublicInstance | undefined>();

const computeTaxes = (property: 'fees' | 'vat' | 'otherTax'): number => {
  const baseOfferAmount =
    offer.value?.pricing?.amount?.[property]?.amount?.excluded ?? 0;

  const baseOptionsAmount = options.value.reduce(
    (acc, option) =>
      acc +
      // @ts-ignore
      (option.apiOption?.pricing?.amount?.[property]?.amount?.excluded ?? 0),
    0,
  );

  return baseOfferAmount + baseOptionsAmount;
};

const hotelCurrency = computed(() => offer.value?.pricing?.currency as string);

const offerAmount = computed(() => offer.value?.pricing?.amount?.afterTax || 0);

const optionsAmount = computed(() =>
  options.value.reduce(
    (sum, option) =>
      sum +
      (option?.apiOption?.pricing?.amount?.hotelKeeper || 0) *
        option.bookingOption.quantity,
    0,
  ),
);

const guestsLabel = computed(() => {
  let baseString = t('booking.recap.rooms.titleContext', {
    totalRooms: bookingStore.numberOfRooms,
    count: adults,
  });

  if (childrenAges.length) {
    baseString += t('booking.recap.rooms.titleContextChildren', {
      count: childrenAges.length,
    });
  }

  return baseString;
});

const totalCost = computed(() => offerAmount.value + optionsAmount.value);

const feesTaxes = computed(() => computeTaxes('fees'));
const vatTaxes = computed(() => computeTaxes('vat'));
const otherTaxTaxes = computed(() => computeTaxes('otherTax'));

const userCurrency = computed(() => bookingStore.currentCurrency);

const offerHotelKeeper = computed(
  () => offer.value?.pricing?.amount?.hotelKeeper ?? 0,
);
const convertedOfferPrice = useConvertToUserCurrency(
  offerHotelKeeper,
  hotelCurrency,
  userCurrency,
);

const convertedTotal = useConvertToUserCurrency(
  totalCost,
  hotelCurrency,
  userCurrency,
);

const convertedFeesTaxes = useConvertToUserCurrency(
  feesTaxes,
  hotelCurrency,
  userCurrency,
);

const convertedVatTaxes = useConvertToUserCurrency(
  vatTaxes,
  hotelCurrency,
  userCurrency,
);

const convertedOtherTaxTaxes = useConvertToUserCurrency(
  otherTaxTaxes,
  hotelCurrency,
  userCurrency,
);

const currentOfferCode = computed(() => {
  return (userStore.isLogged || wantSignup.value) &&
    props.bookingRoom.memberOfferId
    ? props.bookingRoom.memberOfferId
    : props.bookingRoom.publicOfferId;
});

const openRateConditions = () => {
  let currentOfferCode = props.bookingRoom.publicOfferId;

  if (props.bookingRoom.memberOfferId) {
    currentOfferCode = props.bookingRoom.memberOfferId;
  }

  load(document.value, {
    adults,
    dateIn: bookingStore.dates.dateIn as string,
    hotelCode: bookingStore.hotelId as string,
    nights: bookingStore.dates.nights,
    productCode: props.bookingRoom.productCode,
    offerCode: currentOfferCode,
  });

  drawerOpen.value = true;
};

onResult(({ data }) => {
  if (!data || !data.roomOfferDetails?.offers?.length) {
    return [];
  }

  const { offers: roomOffers, detail, label } = data.roomOfferDetails;
  const hasMemberPrice = roomOffers.length === 2;

  rateProps.value = {
    currency: roomOffers[0]?.pricing?.currency ?? '',
    detailsPerNight: detail as RoomOfferDetailFragment[],
    mealPlan: roomOffers[0]?.mealPlan ?? '',
    policies: roomOffers[0]?.policies ?? [],
    rateDescription: roomOffers[0]?.description ?? '',
    rateTitle: roomOffers[0]?.label ?? '',
    roomTitle: label ?? '',
    roomTotalMemberPrice: hasMemberPrice
      ? (roomOffers[0]?.pricing?.amount?.hotelKeeper as number)
      : 0,
    roomTotalPrice: hasMemberPrice
      ? (roomOffers[1]?.pricing?.amount?.hotelKeeper as number)
      : (roomOffers[0]?.pricing?.amount?.hotelKeeper as number),
    taxes: roomOffers[0]?.tax?.map(tax => tax?.label ?? ''),
  };
});

function getEnhancementPrice(
  enhancement: BookingRoomOptionEnhancement,
): number {
  return (
    (areAllTaxesIncluded
      ? enhancement?.apiOption?.pricing?.amount?.afterTax
      : enhancement?.apiOption?.pricing?.amount?.hotelKeeper) ?? 0
  );
}

defineExpose({ accommodation, options, offer });
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/constants';

.BookingRecapRoom-title {
  display: inline-block;
  margin-bottom: constants.$inner-01;
}

.BookingRecapRoom-expand {
  align-items: flex-start;
}

.BookingRecapRoom-grow {
  width: 100%;
}

.BookingRecapRoom-alignRight {
  text-align: right;
}

.BookingRecapRoom-row {
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  :nth-child(2) {
    flex-shrink: 0;
  }
}

.BookingRecapRoom-breakdown {
  margin-top: constants.$margin-01;

  &--reverse {
    margin-top: 0;
    margin-bottom: constants.$margin-01;
  }
}

.BookingRecapRoom-caption {
  text-align: left;
}

.BookingRecapRoom-rateConditions {
  text-transform: uppercase;
}
</style>
